import { useAppStore } from '../store';
import { motion, AnimatePresence } from 'framer-motion';
import { useMemo, useRef, useState, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, Legend, LabelList
} from 'recharts';
import { BarChart3, PieChart as PieChartIcon, TrendingUp, Target, Download, FileImage, FileText, Loader2, X, Info } from 'lucide-react';
import { exportAsPNG, exportAsPDF, exportAllChartsAsPDF } from '../utils/exportChart';

const COLORS = {
  sage: '#7A9E7E',
  bronze: '#C4A77D',
  terracotta: '#D4A574',
  steel: '#6B7B8C',
  cedar: '#9B8AA6',
};

const TIER_LABELS: Record<string, string> = {
  knowledge: '知识理解',
  application: '应用分析',
  analysis: '综合评估',
  evaluation: '拓展延伸',
};

// Animation config for Recharts
const chartAnimationConfig = {
  duration: 800,
  easing: 'ease-out' as const,
};

// Enhanced tooltip with animation
function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string; dataKey?: string }>; label?: string }) {
  if (active && payload && payload.length) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 5 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 5 }}
        transition={{ duration: 0.15 }}
        className="bg-white/98 backdrop-blur-md border border-stone-200/80 rounded-xl px-4 py-3 shadow-xl shadow-stone-200/50"
      >
        <p className="text-xs font-semibold text-stone-800 mb-2 pb-2 border-b border-stone-100">{label}</p>
        <div className="space-y-1.5">
          {payload.map((entry, index) => (
            <motion.p
              key={index}
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="text-[11px] flex items-center gap-2"
            >
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-stone-500">{entry.name}:</span>
              <span className="font-semibold" style={{ color: entry.color }}>{entry.value}</span>
            </motion.p>
          ))}
        </div>
      </motion.div>
    );
  }
  return null;
}

// Detail modal for chart data
function ChartDetailModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-stone-950/20 backdrop-blur-sm z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.3 }}
            className="fixed inset-4 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-md bg-white rounded-2xl shadow-2xl z-50 overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-stone-100">
              <h3 className="text-base font-display text-stone-800">{title}</h3>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 transition-colors"
              >
                <X className="w-4 h-4 text-stone-400" />
              </button>
            </div>
            <div className="p-4 max-h-[70vh] overflow-auto">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function getDayName(dateStr: string): string {
  const date = new Date(dateStr);
  const days = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  return days[date.getDay()];
}

// Export button component
function ExportButton({
  onExportPNG,
  onExportPDF,
  isLoading
}: {
  onExportPNG: () => Promise<void>;
  onExportPDF: () => Promise<void>;
  isLoading: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-lg transition-all touch-target"
      >
        {isLoading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Download className="w-3.5 h-3.5" />
        )}
        导出
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full mt-1 z-20 bg-white rounded-xl shadow-xl border border-stone-200/80 overflow-hidden min-w-[140px]"
            >
              <button
                onClick={async () => {
                  setIsOpen(false);
                  await onExportPNG();
                }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-stone-600 hover:bg-sage/10 transition-colors"
              >
                <FileImage className="w-3.5 h-3.5 text-sage" />
                导出为 PNG
              </button>
              <button
                onClick={async () => {
                  setIsOpen(false);
                  await onExportPDF();
                }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-stone-600 hover:bg-bronze/10 transition-colors"
              >
                <FileText className="w-3.5 h-3.5 text-bronze" />
                导出为 PDF
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// Chart card wrapper with hover effect
function ChartCard({
  children,
  chartRef,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  chartRef?: React.RefObject<HTMLDivElement>;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      ref={chartRef}
      className={`card-premium p-4 sm:p-5 bg-white group ${className}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -2 }}
    >
      {children}
    </motion.div>
  );
}

export function WeeklyActivityChart() {
  const { studySessions, activeSubjectId } = useAppStore();
  const chartRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeDataIndex, setActiveDataIndex] = useState<number | null>(null);

  const weeklyData = useMemo(() => {
    const subjectSessions = studySessions.filter(s => s.subjectId === activeSubjectId);

    const groupedByDate = subjectSessions.reduce((acc, session) => {
      if (!acc[session.date]) {
        acc[session.date] = { hours: 0, questions: 0, review: 0, correct: 0 };
      }
      acc[session.date].hours += session.duration / 60;
      acc[session.date].questions += session.questionsCompleted;
      acc[session.date].review += session.flashcardsReviewed;
      acc[session.date].correct += session.questionsCorrect;
      return acc;
    }, {} as Record<string, { hours: number; questions: number; review: number; correct: number }>);

    const sortedData = Object.entries(groupedByDate)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-7)
      .map(([date, data]) => ({
        day: getDayName(date),
        date: date.slice(5),
        fullDate: date,
        hours: Number(data.hours.toFixed(1)),
        questions: data.questions,
        review: data.review,
        correct: data.correct,
        accuracy: data.questions > 0 ? ((data.correct / data.questions) * 100).toFixed(0) : 0,
      }));

    return sortedData.length > 0 ? sortedData : [
      { day: '周一', date: '', fullDate: '', hours: 0, questions: 0, review: 0, correct: 0, accuracy: 0 },
      { day: '周二', date: '', fullDate: '', hours: 0, questions: 0, review: 0, correct: 0, accuracy: 0 },
      { day: '周三', date: '', fullDate: '', hours: 0, questions: 0, review: 0, correct: 0, accuracy: 0 },
      { day: '周四', date: '', fullDate: '', hours: 0, questions: 0, review: 0, correct: 0, accuracy: 0 },
      { day: '周五', date: '', fullDate: '', hours: 0, questions: 0, review: 0, correct: 0, accuracy: 0 },
      { day: '周六', date: '', fullDate: '', hours: 0, questions: 0, review: 0, correct: 0, accuracy: 0 },
      { day: '周日', date: '', fullDate: '', hours: 0, questions: 0, review: 0, correct: 0, accuracy: 0 },
    ];
  }, [studySessions, activeSubjectId]);

  const totals = useMemo(() => {
    const subjectSessions = studySessions.filter(s => s.subjectId === activeSubjectId);
    return {
      totalHours: subjectSessions.reduce((sum, s) => sum + s.duration, 0) / 60,
      totalQuestions: subjectSessions.reduce((sum, s) => sum + s.questionsCompleted, 0),
      totalCorrect: subjectSessions.reduce((sum, s) => sum + s.questionsCorrect, 0),
      avgAccuracy: subjectSessions.length > 0
        ? (subjectSessions.reduce((sum, s) => sum + s.questionsCorrect, 0) /
           subjectSessions.reduce((sum, s) => sum + s.questionsCompleted, 0) * 100).toFixed(0)
        : 0,
    };
  }, [studySessions, activeSubjectId]);

  const handleExportPNG = async () => {
    if (!chartRef.current) return;
    setIsExporting(true);
    try {
      await exportAsPNG(chartRef.current, { filename: 'weekly-activity.png' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!chartRef.current) return;
    setIsExporting(true);
    try {
      await exportAsPDF(chartRef.current, { filename: 'weekly-activity.pdf' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <ChartCard chartRef={chartRef} delay={0.2}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-display flex items-center gap-2.5">
            <motion.div
              className="w-7 h-7 rounded-lg bg-sage/10 flex items-center justify-center"
              whileHover={{ scale: 1.1, rotate: 5 }}
            >
              <TrendingUp className="w-4 h-4 text-sage" />
            </motion.div>
            本周学习活动
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDetailOpen(true)}
              className="w-7 h-7 flex items-center justify-center text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
            <ExportButton
              onExportPNG={handleExportPNG}
              onExportPDF={handleExportPDF}
              isLoading={isExporting}
            />
          </div>
        </div>

        <div className="h-48 sm:h-56">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <AreaChart
              data={weeklyData}
              margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
              onMouseMove={(_, e) => {
                if (e?.activeTooltipIndex !== undefined) {
                  setActiveDataIndex(e.activeTooltipIndex);
                }
              }}
              onMouseLeave={() => setActiveDataIndex(null)}
            >
              <defs>
                <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLORS.sage} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={COLORS.sage} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorQuestions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLORS.bronze} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={COLORS.bronze} stopOpacity={0} />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,25,23,0.06)" vertical={false} />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 10, fill: '#78716c' }}
                axisLine={false}
                tickLine={false}
                tickMargin={8}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#78716c' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={<CustomTooltip />}
                animationDuration={200}
                cursor={{ stroke: 'rgba(28,25,23,0.1)', strokeWidth: 1 }}
              />
              <Area
                type="monotone"
                dataKey="hours"
                name="学习时长(h)"
                stroke={COLORS.sage}
                strokeWidth={activeDataIndex !== null ? 3 : 2}
                fillOpacity={1}
                fill="url(#colorHours)"
                animationDuration={chartAnimationConfig.duration}
                animationEasing={chartAnimationConfig.easing}
                dot={{ r: 0 }}
                activeDot={{
                  r: 6,
                  stroke: COLORS.sage,
                  strokeWidth: 2,
                  fill: 'white',
                  filter: 'url(#glow)'
                }}
              />
              <Area
                type="monotone"
                dataKey="questions"
                name="完成题目"
                stroke={COLORS.bronze}
                strokeWidth={activeDataIndex !== null ? 3 : 2}
                fillOpacity={1}
                fill="url(#colorQuestions)"
                animationDuration={chartAnimationConfig.duration}
                animationEasing={chartAnimationConfig.easing}
                dot={{ r: 0 }}
                activeDot={{
                  r: 6,
                  stroke: COLORS.bronze,
                  strokeWidth: 2,
                  fill: 'white',
                  filter: 'url(#glow)'
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4">
          {[
            { value: `${totals.totalHours.toFixed(1)}h`, label: '累计学习', color: 'sage' },
            { value: totals.totalQuestions, label: '完成题目', color: 'bronze' },
            { value: `${totals.avgAccuracy}%`, label: '平均正确率', color: 'steel' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              className="text-center p-2.5 rounded-xl bg-stone-50/80 hover:bg-stone-100/80 transition-colors cursor-default"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <p className="text-lg font-display text-stone-800">{stat.value}</p>
              <p className="text-[10px] text-stone-400">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="flex items-center justify-center gap-6 mt-3 text-[10px] text-stone-500">
          <motion.span
            className="flex items-center gap-1.5 cursor-pointer hover:text-sage transition-colors"
            whileHover={{ scale: 1.05 }}
          >
            <span className="w-2.5 h-2.5 rounded-full bg-sage" />
            学习时长
          </motion.span>
          <motion.span
            className="flex items-center gap-1.5 cursor-pointer hover:text-bronze transition-colors"
            whileHover={{ scale: 1.05 }}
          >
            <span className="w-2.5 h-2.5 rounded-full bg-bronze" />
            完成题目
          </motion.span>
        </div>
      </ChartCard>

      <ChartDetailModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="学习活动详情"
      >
        <div className="space-y-3">
          {weeklyData.map((day, i) => (
            <motion.div
              key={day.fullDate || i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className="p-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-stone-700">{day.day}</span>
                <span className="text-xs text-stone-400">{day.date}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-stone-400">学习时长：</span>
                  <span className="font-medium text-sage">{day.hours}h</span>
                </div>
                <div>
                  <span className="text-stone-400">完成题目：</span>
                  <span className="font-medium text-bronze">{day.questions}</span>
                </div>
                <div>
                  <span className="text-stone-400">正确率：</span>
                  <span className="font-medium text-steel">{day.accuracy}%</span>
                </div>
                <div>
                  <span className="text-stone-400">闪读卡：</span>
                  <span className="font-medium text-cedar">{day.review}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </ChartDetailModal>
    </>
  );
}

export function MasteryDistributionChart() {
  const { units, activeSubjectId } = useAppStore();
  const chartRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const subjectUnits = units.filter(u => u.subjectId === activeSubjectId);
  const allPoints = subjectUnits.flatMap(u => u.knowledgePoints);

  const pieData = useMemo(() => {
    const masteryLevels = {
      excellent: allPoints.filter(p => p.mastery >= 80),
      good: allPoints.filter(p => p.mastery >= 60 && p.mastery < 80),
      fair: allPoints.filter(p => p.mastery >= 40 && p.mastery < 60),
      weak: allPoints.filter(p => p.mastery < 40),
    };

    return [
      { name: '优秀 (≥80%)', value: masteryLevels.excellent.length, color: COLORS.sage, points: masteryLevels.excellent },
      { name: '良好 (60-79%)', value: masteryLevels.good.length, color: COLORS.bronze, points: masteryLevels.good },
      { name: '一般 (40-59%)', value: masteryLevels.fair.length, color: COLORS.steel, points: masteryLevels.fair },
      { name: '薄弱 (<40%)', value: masteryLevels.weak.length, color: COLORS.terracotta, points: masteryLevels.weak },
    ].filter(d => d.value > 0);
  }, [allPoints]);

  const totalPoints = allPoints.length;
  const avgMastery = allPoints.length > 0
    ? (allPoints.reduce((sum, p) => sum + p.mastery, 0) / allPoints.length).toFixed(0)
    : 0;

  const handleExportPNG = async () => {
    if (!chartRef.current) return;
    setIsExporting(true);
    try {
      await exportAsPNG(chartRef.current, { filename: 'mastery-distribution.png' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!chartRef.current) return;
    setIsExporting(true);
    try {
      await exportAsPDF(chartRef.current, { filename: 'mastery-distribution.pdf' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <ChartCard chartRef={chartRef} delay={0.3}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-display flex items-center gap-2.5">
            <motion.div
              className="w-7 h-7 rounded-lg bg-bronze/10 flex items-center justify-center"
              whileHover={{ scale: 1.1, rotate: -5 }}
            >
              <PieChartIcon className="w-4 h-4 text-bronze" />
            </motion.div>
            知识点掌握分布
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDetailOpen(true)}
              className="w-7 h-7 flex items-center justify-center text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
            <ExportButton
              onExportPNG={handleExportPNG}
              onExportPDF={handleExportPDF}
              isLoading={isExporting}
            />
          </div>
        </div>

        <div className="h-48 sm:h-56 flex items-center justify-center relative">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={activeIndex !== null ? 80 : 75}
                paddingAngle={3}
                dataKey="value"
                animationDuration={chartAnimationConfig.duration}
                animationEasing={chartAnimationConfig.easing}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {pieData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    stroke="none"
                    style={{
                      filter: activeIndex === index ? 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))' : 'none',
                      transform: activeIndex === index ? 'scale(1.02)' : 'scale(1)',
                      transformOrigin: 'center',
                      transition: 'all 0.2s ease',
                    }}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Center label */}
          <motion.div
            className="absolute pointer-events-none text-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
          >
            <p className="text-2xl font-display text-stone-800">{avgMastery}%</p>
            <p className="text-[10px] text-stone-400">平均掌握度</p>
          </motion.div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3">
          {pieData.map((entry, index) => (
            <motion.div
              key={index}
              className="flex items-center gap-2 text-[10px] text-stone-500 px-2 py-1.5 rounded-lg hover:bg-stone-50 transition-colors cursor-pointer"
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
              whileHover={{ scale: 1.02 }}
              style={{
                backgroundColor: activeIndex === index ? 'rgba(28,25,23,0.04)' : 'transparent'
              }}
            >
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              {entry.name}: {entry.value}个
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3">
          <motion.div
            className="text-center p-2 rounded-xl bg-stone-50/50"
            whileHover={{ scale: 1.02 }}
          >
            <p className="text-lg font-display text-stone-800">{totalPoints}</p>
            <p className="text-[10px] text-stone-400">知识点总数</p>
          </motion.div>
          <motion.div
            className="text-center p-2 rounded-xl bg-stone-50/50"
            whileHover={{ scale: 1.02 }}
          >
            <p className="text-lg font-display text-stone-800">{avgMastery}%</p>
            <p className="text-[10px] text-stone-400">平均掌握度</p>
          </motion.div>
        </div>
      </ChartCard>

      <ChartDetailModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="知识点掌握详情"
      >
        <div className="space-y-4">
          {pieData.map((level, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: level.color }}
                />
                <span className="text-sm font-medium text-stone-700">{level.name}</span>
                <span className="text-xs text-stone-400">({level.value}个)</span>
              </div>
              <div className="space-y-1 pl-5">
                {level.points.slice(0, 5).map((point, j) => (
                  <div
                    key={point.id}
                    className="flex items-center justify-between text-xs p-2 rounded-lg bg-stone-50 hover:bg-stone-100 transition-colors"
                  >
                    <span className="text-stone-600 truncate max-w-[60%]">{point.title}</span>
                    <span className="font-medium" style={{ color: level.color }}>{point.mastery}%</span>
                  </div>
                ))}
                {level.points.length > 5 && (
                  <p className="text-[10px] text-stone-400 pl-2">
                    还有 {level.points.length - 5} 个知识点...
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </ChartDetailModal>
    </>
  );
}

export function QuestionTypePerformanceChart() {
  const { questionPerformance } = useAppStore();
  const chartRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [activeBarIndex, setActiveBarIndex] = useState<number | null>(null);

  const barData = useMemo(() => {
    return questionPerformance.map(qp => ({
      type: TIER_LABELS[qp.tier] || qp.tier,
      rate: qp.total > 0 ? Number(((qp.correct / qp.total) * 100).toFixed(0)) : 0,
      correct: qp.correct,
      total: qp.total,
      incorrect: qp.incorrect,
    }));
  }, [questionPerformance]);

  const overallAccuracy = useMemo(() => {
    const totalQuestions = questionPerformance.reduce((sum, qp) => sum + qp.total, 0);
    const totalCorrect = questionPerformance.reduce((sum, qp) => sum + qp.correct, 0);
    return totalQuestions > 0 ? ((totalCorrect / totalQuestions) * 100).toFixed(0) : 0;
  }, [questionPerformance]);

  const handleExportPNG = async () => {
    if (!chartRef.current) return;
    setIsExporting(true);
    try {
      await exportAsPNG(chartRef.current, { filename: 'question-performance.png' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!chartRef.current) return;
    setIsExporting(true);
    try {
      await exportAsPDF(chartRef.current, { filename: 'question-performance.pdf' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <ChartCard chartRef={chartRef} delay={0.4}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-display flex items-center gap-2.5">
          <motion.div
            className="w-7 h-7 rounded-lg bg-steel-50 flex items-center justify-center"
            whileHover={{ scale: 1.1, rotate: 5 }}
          >
            <BarChart3 className="w-4 h-4 text-steel" />
          </motion.div>
          题型表现分析
        </h3>
        <ExportButton
          onExportPNG={handleExportPNG}
          onExportPDF={handleExportPDF}
          isLoading={isExporting}
        />
      </div>

      <div className="h-48 sm:h-56">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <BarChart
            data={barData}
            margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
            onMouseMove={(_, e) => {
              if (e?.activeTooltipIndex !== undefined) {
                setActiveBarIndex(e.activeTooltipIndex);
              }
            }}
            onMouseLeave={() => setActiveBarIndex(null)}
          >
            <defs>
              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS.sage} stopOpacity={1} />
                <stop offset="100%" stopColor={COLORS.sage} stopOpacity={0.7} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,25,23,0.06)" vertical={false} />
            <XAxis
              dataKey="type"
              tick={{ fontSize: 9, fill: '#78716c' }}
              axisLine={false}
              tickLine={false}
              tickMargin={8}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#78716c' }}
              axisLine={false}
              tickLine={false}
              domain={[0, 100]}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'rgba(28,25,23,0.04)' }}
              animationDuration={200}
            />
            <Bar
              dataKey="rate"
              name="正确率%"
              fill="url(#barGradient)"
              radius={[6, 6, 0, 0]}
              barSize={36}
              animationDuration={chartAnimationConfig.duration}
              animationEasing={chartAnimationConfig.easing}
            >
              <LabelList
                dataKey="rate"
                position="top"
                formatter={(value: number) => `${value}%`}
                style={{ fontSize: 10, fill: '#78716c', fontWeight: 600 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        {barData.map((item, i) => (
          <motion.div
            key={i}
            className="text-center p-2.5 rounded-xl bg-stone-50/50 hover:bg-stone-100/50 transition-colors cursor-default"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 + i * 0.1 }}
            whileHover={{ scale: 1.03, y: -2 }}
            style={{
              backgroundColor: activeBarIndex === i ? 'rgba(122,158,126,0.1)' : undefined
            }}
          >
            <p className="text-lg font-display text-stone-800">{item.rate}%</p>
            <p className="text-[10px] text-stone-400">{item.correct}/{item.total}题</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="text-center mt-4 p-3 rounded-xl bg-gradient-to-br from-sage/10 to-sage/5"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <p className="text-sm text-stone-500">整体正确率</p>
        <motion.p
          className="text-2xl font-display text-sage"
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.8, type: 'spring' }}
        >
          {overallAccuracy}%
        </motion.p>
      </motion.div>
    </ChartCard>
  );
}

export function AssessmentRadarChart() {
  const { progress, activeSubjectId } = useAppStore();
  const chartRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const currentProgress = progress.find(p => p.subjectId === activeSubjectId);

  const radarData = useMemo(() => {
    if (!currentProgress?.assessmentScores) {
      return [
        { criterion: '知识与理解', score: 0, fullMark: 100 },
        { criterion: '应用能力', score: 0, fullMark: 100 },
        { criterion: '分析思维', score: 0, fullMark: 100 },
        { criterion: '综合评估', score: 0, fullMark: 100 },
        { criterion: '语言表达', score: 0, fullMark: 100 },
        { criterion: '批判思维', score: 0, fullMark: 100 },
      ];
    }

    const criterionMap: Record<string, string> = {
      'A': '知识与理解',
      'B': '探究与设计',
      'C': '处理与评估',
      'D': '科学态度',
    };

    return currentProgress.assessmentScores.map(score => ({
      criterion: criterionMap[score.criterion] || score.label?.split(' ')[0] || score.criterion,
      score: score.currentScore ? (score.currentScore / score.maxScore) * 100 : 0,
      fullMark: 100,
      description: score.description,
    }));
  }, [currentProgress]);

  const overallScore = useMemo(() => {
    if (!currentProgress?.assessmentScores) return 0;
    const totalScore = currentProgress.assessmentScores.reduce((sum, s) => sum + (s.currentScore || 0), 0);
    const maxScore = currentProgress.assessmentScores.reduce((sum, s) => sum + s.maxScore, 0);
    return maxScore > 0 ? ((totalScore / maxScore) * 100).toFixed(0) : 0;
  }, [currentProgress]);

  const handleExportPNG = async () => {
    if (!chartRef.current) return;
    setIsExporting(true);
    try {
      await exportAsPNG(chartRef.current, { filename: 'assessment-radar.png' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!chartRef.current) return;
    setIsExporting(true);
    try {
      await exportAsPDF(chartRef.current, { filename: 'assessment-radar.pdf' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <ChartCard chartRef={chartRef} delay={0.5}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-display flex items-center gap-2.5">
          <motion.div
            className="w-7 h-7 rounded-lg bg-cedar-50 flex items-center justify-center"
            whileHover={{ scale: 1.1, rotate: -5 }}
          >
            <Target className="w-4 h-4 text-cedar" />
          </motion.div>
          IB 评估维度雷达图
        </h3>
        <ExportButton
          onExportPNG={handleExportPNG}
          onExportPDF={handleExportPDF}
          isLoading={isExporting}
        />
      </div>

      <div className="h-48 sm:h-56">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <RadarChart cx="50%" cy="50%" outerRadius="65%" data={radarData}>
            <defs>
              <linearGradient id="radarGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS.sage} stopOpacity={0.5} />
                <stop offset="100%" stopColor={COLORS.sage} stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <PolarGrid stroke="rgba(28,25,23,0.08)" />
            <PolarAngleAxis
              dataKey="criterion"
              tick={{ fontSize: 9, fill: '#78716c' }}
            />
            <PolarRadiusAxis
              angle={30}
              domain={[0, 100]}
              tick={{ fontSize: 8, fill: '#a8a29e' }}
              axisLine={false}
            />
            <Radar
              name="得分"
              dataKey="score"
              stroke={COLORS.sage}
              fill="url(#radarGradient)"
              strokeWidth={2}
              animationDuration={chartAnimationConfig.duration}
              animationEasing={chartAnimationConfig.easing}
            />
            <Tooltip content={<CustomTooltip />} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center justify-center gap-4 mt-3 text-[10px] text-stone-500">
        <motion.span
          className="flex items-center gap-1.5"
          whileHover={{ scale: 1.05 }}
        >
          <span className="w-2.5 h-2.5 rounded-full bg-sage" />
          当前水平
        </motion.span>
        <motion.span
          className="flex items-center gap-1.5"
          whileHover={{ scale: 1.05 }}
        >
          <span className="w-2.5 h-2.5 rounded-full border border-stone-300" />
          满分 100
        </motion.span>
      </div>

      <motion.div
        className="text-center mt-4 p-3 rounded-xl bg-gradient-to-br from-cedar/10 to-cedar/5"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <p className="text-sm text-stone-500">综合评估得分</p>
        <motion.p
          className="text-2xl font-display text-cedar"
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.8, type: 'spring' }}
        >
          {overallScore}%
        </motion.p>
      </motion.div>
    </ChartCard>
  );
}

export default function StatsCharts() {
  const weeklyRef = useRef<HTMLDivElement>(null);
  const masteryRef = useRef<HTMLDivElement>(null);
  const questionRef = useRef<HTMLDivElement>(null);
  const radarRef = useRef<HTMLDivElement>(null);
  const [isExportingAll, setIsExportingAll] = useState(false);

  const handleExportAllPDF = async () => {
    const elements = [weeklyRef.current, masteryRef.current, questionRef.current, radarRef.current]
      .filter((el): el is HTMLDivElement => el !== null);

    if (elements.length === 0) return;

    setIsExportingAll(true);
    try {
      await exportAllChartsAsPDF(elements, { filename: 'learning-report.pdf' });
    } finally {
      setIsExportingAll(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Export all button */}
      <motion.div
        className="flex justify-end"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <motion.button
          onClick={handleExportAllPDF}
          disabled={isExportingAll}
          className="flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-white bg-gradient-to-r from-sage to-sage-dark rounded-xl hover:shadow-lg hover:shadow-sage/20 transition-shadow touch-target disabled:opacity-50"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {isExportingAll ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileText className="w-4 h-4" />
          )}
          导出完整学习报告
        </motion.button>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div ref={weeklyRef}>
          <WeeklyActivityChart />
        </div>
        <div ref={masteryRef}>
          <MasteryDistributionChart />
        </div>
        <div ref={questionRef}>
          <QuestionTypePerformanceChart />
        </div>
        <div ref={radarRef}>
          <AssessmentRadarChart />
        </div>
      </div>
    </div>
  );
}
